import React, {
  createContext,
  forwardRef,
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef
} from "react"
import type { ReactNode } from "react"
import {
  useFloating,
  useClick,
  useDismiss,
  useRole,
  useListNavigation,
  useInteractions,
  useId,
  FloatingFocusManager,
  useTypeahead,
  flip,
  offset,
  size,
  autoUpdate,
  FloatingList,
  useMergeRefs
} from "@floating-ui/react"
import cx from "classnames"
import { omit } from "lodash"
import Label from "../label"
import Icon from "../icon"
import Portal from "../portal"
import Option from "./option"
import { jsxTextContent } from "./util"

const DEFAULT_BTN_CLASSES =
  "inline-flex items-center border transition-colors delay-75 text-sm gap-2 justify-between rounded select-none pr-2"
export const MENU_CLASSES =
  "bg-white p-2 shadow-lg outline-none overflow-auto border border-gray-200 rounded-lg z-10 overflow-x-hidden"
export const MENU_OFFSET = 4
export const MAX_MENU_HEIGHT = 300
export const MIN_MENU_WIDTH = 192

export type SelectContextValue = {
  activeIndex: number | null
  selectedIndex?: number | null
  setSelectedIndex?: (index: number | null) => void
  getItemProps: ReturnType<typeof useInteractions>["getItemProps"]
  handleSelect: (index: number) => void
  inputValue?: string
}

export const SelectContext = createContext<SelectContextValue>({
  activeIndex: null,
  selectedIndex: null,
  getItemProps: () => ({}),
  handleSelect: () => {}
})

export type SelectOption = {
  /** Display text for the option */
  label: ReactNode
  /** Value submitted when this option is selected */
  value: string | number
  /** Whether this option is non-interactive */
  disabled?: boolean
  /** Custom component to render instead of the label */
  component?: ReactNode
  /** Explicit React key for the option element */
  key?: string
}

export type StandaloneSelectProps = Omit<ComposableSelectProps, "children"> & {
  /** List of options to display */
  options: (SelectOption | ReactNode)[]
  /** Currently selected value */
  value?: string | number
}

export const StandaloneSelect = forwardRef<
  HTMLDivElement,
  StandaloneSelectProps
>((props, ref) => {
  const { options, value, ...otherProps } = props
  const selectableOptions = (options as SelectOption[]).filter(
    option => option.value !== undefined
  )
  const selected = selectableOptions.find(option => option.value === value)

  return (
    <ComposableSelect {...otherProps} ref={ref}>
      {options.map((option, index) => {
        const opt = option as SelectOption
        if (opt.value === undefined)
          return <div key={index}>{option as ReactNode}</div>

        return (
          <Option
            key={`${opt.label}-${opt.value}`}
            value={opt.value}
            selected={selected?.value === opt.value}
            label={opt.label}
            disabled={opt.disabled}
          >
            {opt.component || opt.label}
          </Option>
        )
      })}
    </ComposableSelect>
  )
})

const excludeSeparators = (child: React.ReactElement) =>
  child.props?.children?.props?.role !== "separator"

export type ComposableSelectProps = {
  /** Additional CSS classes */
  className?: string
  /** When true, removes border and uses tertiary button styling */
  borderless?: boolean
  /** Visible label displayed above the select */
  label?: string
  /** Accessible label for screen readers */
  "aria-label"?: string
  /** ID of an external element that labels this select */
  "aria-labelledby"?: string
  /** Name attribute, included in the onChange event target */
  name?: string
  /** Whether the select is in an error state */
  error?: boolean
  /** Whether the select is disabled */
  disabled?: boolean
  /** Called when a new option is selected */
  onChange?: (event: {
    target: { label: ReactNode; value: string | number; name?: string }
  }) => void
  /** Option elements to render in the dropdown */
  children?: ReactNode
  /** Text shown when no option is selected */
  placeholder?: string
  /** Whether to hide the dropdown caret icon */
  hideCaretIcon?: boolean
  /** Whether the dropdown menu is scrollable */
  isScrollable?: boolean
}

export const ComposableSelect = forwardRef<
  HTMLDivElement,
  ComposableSelectProps
>(
  (
    {
      label,
      children,
      className,
      onChange,
      disabled,
      placeholder = "Select",
      hideCaretIcon = false,
      isScrollable = true,
      borderless = false,
      ...props
    },
    forwardedRef
  ) => {
    const labelId = useId()
    const [isOpen, setIsOpen] = useState(false)
    const [activeIndex, setActiveIndex] = useState<number | null>(null)
    const childNodes = useMemo(
      () =>
        (React.Children.toArray(children) as React.ReactElement[]).filter(
          excludeSeparators
        ),
      [children]
    )

    const [selectedIndex, setSelectedIndex] = useState<number | null>(() => {
      const selected = childNodes.findIndex(child => child.props.selected)
      return selected === -1 ? null : selected
    })
    const [selectedLabel, setSelectedLabel] = useState<ReactNode>(() => {
      if (selectedIndex !== null) {
        return (
          childNodes[selectedIndex].props.label ||
          childNodes[selectedIndex].props.children
        )
      }
      return null
    })

    // Update selected label when selected index changes
    useEffect(() => {
      const newIndex = childNodes.findIndex(child => child.props.selected)
      if (newIndex === -1) {
        setSelectedIndex(null)
        setSelectedLabel(null)
        return
      }
      setSelectedIndex(newIndex)
      setSelectedLabel(
        childNodes[newIndex].props.label || childNodes[newIndex].props.children
      )
    }, [childNodes])

    const elementsRef = useRef<(HTMLElement | null)[]>([])
    const labelsRef = useRef<(string | null)[]>([])

    const { refs, floatingStyles, context } = useFloating({
      placement: "bottom-start",
      open: isOpen,
      onOpenChange: setIsOpen,
      whileElementsMounted: autoUpdate,
      middleware: [
        flip(),
        size({
          apply({ rects, elements }) {
            Object.assign(elements.floating.style, {
              minWidth: `${rects.reference.width}px`
            })
          }
        }),
        offset(MENU_OFFSET)
      ]
    })

    const handleSelect = useCallback(
      (index: number) => {
        setSelectedIndex(index)
        if (onChange) {
          const { children, label, value } = childNodes[index].props
          onChange({
            target: {
              label: label || children,
              value,
              name: props.name
            }
          })
        }
        setIsOpen(false)
        if (index !== null) {
          setSelectedLabel(labelsRef.current[index])
        }
      },
      [onChange, childNodes, props.name]
    )

    const handleTypeaheadMatch = (index: number | null) => {
      if (isOpen) {
        setActiveIndex(index)
      } else {
        if (index !== null) handleSelect(index)
      }
    }

    const listNav = useListNavigation(context, {
      listRef: elementsRef,
      activeIndex,
      selectedIndex,
      onNavigate: setActiveIndex
    })
    const typeahead = useTypeahead(context, {
      enabled: !disabled,
      listRef: labelsRef,
      activeIndex,
      selectedIndex,
      onMatch: handleTypeaheadMatch,
      // Overrides the default findMatch to match strings within the label and not just the start
      // It also gracefully handles the case where the label is a React element
      findMatch: (list, query) => {
        return (
          list.find(label => {
            const labelString = jsxTextContent(label)
            return labelString.toLowerCase().includes(query)
          }) ?? null
        )
      }
    })
    const click = useClick(context, { enabled: !disabled })
    const dismiss = useDismiss(context)
    const role = useRole(context, { role: "listbox" })

    const interactions = [listNav, dismiss, role, click, typeahead]

    const { getReferenceProps, getFloatingProps, getItemProps } =
      useInteractions(interactions)

    const selectContext = useMemo(
      () => ({
        activeIndex,
        selectedIndex,
        setSelectedIndex,
        getItemProps,
        handleSelect
      }),
      [activeIndex, selectedIndex, getItemProps, handleSelect]
    )

    const btnProps = omit(props, [
      "aria-label",
      "aria-labelledby",
      "error",
      "value"
    ])
    const btnClassName = cx(DEFAULT_BTN_CLASSES, className, {
      "text-gray-600": !selectedLabel,
      "text-gray-900": selectedLabel,
      "border-blue-500": isOpen && !props.error && !borderless,
      "border-gray-200 hover:border-gray-300":
        !isOpen && !props.error && !borderless,
      "border-none bg-transparent hover:bg-gray-900/[0.06] focus:bg-gray-900/[0.06] cursor-pointer":
        borderless,
      "border-red-500": props.error,
      "bg-gray-50": disabled,
      "bg-gray-900/[0.06]": borderless && isOpen,
      "pl-3": !hideCaretIcon,
      "pl-2": hideCaretIcon // The caret icon has some right padding, so in case it's hidden, we need to reduce the padding left
    })
    return (
      <>
        {label && (
          <Label
            as="div"
            className="mb-1 cursor-default"
            id={labelId}
            onClick={() => (refs.reference.current as HTMLElement).click()}
          >
            {label}
          </Label>
        )}
        <div
          ref={useMergeRefs([refs.setReference, forwardedRef])}
          role="button"
          tabIndex={0}
          {...getReferenceProps()}
          aria-labelledby={label ? labelId : props["aria-labelledby"]}
          aria-label={!label ? props["aria-label"] : undefined}
          aria-disabled={disabled}
          className={btnClassName}
          style={{ paddingBlock: "7px" }}
          {...btnProps}
        >
          <div
            className="truncate"
            title={
              typeof selectedLabel === "string" ? selectedLabel : undefined
            }
          >
            {selectedLabel ?? placeholder}
          </div>
          {!hideCaretIcon && (
            <Icon
              name="down"
              className={cx(
                "duration-75 transition-transform transform text-gray-800",
                { "rotate-180": isOpen }
              )}
              aria-hidden="true"
            />
          )}
        </div>
        <SelectContext.Provider value={selectContext}>
          {isOpen && (
            <FloatingFocusManager context={context} modal={false}>
              <Portal>
                <div
                  ref={refs.setFloating}
                  style={{
                    ...floatingStyles,
                    ...(isScrollable && { maxHeight: MAX_MENU_HEIGHT })
                  }}
                  className={MENU_CLASSES}
                  aria-label="Options"
                  {...getFloatingProps()}
                >
                  <FloatingList elementsRef={elementsRef} labelsRef={labelsRef}>
                    {children}
                  </FloatingList>
                </div>
              </Portal>
            </FloatingFocusManager>
          )}
        </SelectContext.Provider>
      </>
    )
  }
)
